import { IncomeService } from "@interfaces/incomeProfile";
import { ServiceMonthlyIncomePayload } from "@interfaces/serviceMonthly";
import { SERVICE_MONTHLY_INCOME_CODES } from "@helpers/constantes";
/** Rojo de pérdida del diseño de Mercader; no está en el theme. */
export const RED_NEGATIVE = "#B42318";

/** Ancho de los controles de una tarjeta de servicio (`--mp-ctl` del diseño); en móvil, completo. */
export const CONTROL_WIDTH = 176;

const C = SERVICE_MONTHLY_INCOME_CODES;

/**
 * Unidad de cada servicio del catálogo (`code`), para preguntar como habla el médico ("¿cuántas
 * consultas al día?"). Los `monthly` se preguntan al mes (cirugías, estudios…) y se guardan con
 * `period_type: "MONTH"`; los demás, por día (`"DAY"`). El nombre visible lo da el catálogo del backend.
 * `maxPerDay`: tope propio del promedio al día cuando el general (`CardService`) no alcanza.
 */
export const SERVICE_UNITS: Record<string, { one: string; many: string; monthly: boolean; maxPerDay?: number }> = {
  [C.consultas]: { one: "consulta", many: "consultas", monthly: false },
  [C.estudios]: { one: "estudio", many: "estudios", monthly: true },
  // Un laboratorio procesa cientos de análisis al día: tres cifras (notas de Mercader).
  [C.analisis_laboratorio]: { one: "análisis", many: "análisis", monthly: false, maxPerDay: 999 },
  [C.cirugias]: { one: "cirugía", many: "cirugías", monthly: true },
  [C.procedimientos_ambulatorios]: { one: "procedimiento", many: "procedimientos", monthly: true },
  [C.terapias_rehabilitacion]: { one: "sesión", many: "sesiones", monthly: false },
  [C.tratamientos_esteticos]: { one: "tratamiento", many: "tratamientos", monthly: false },
  [C.honorarios_hospitalarios]: { one: "servicio", many: "servicios", monthly: true },
  [C.otro_servicio]: { one: "servicio", many: "servicios", monthly: true },
};

/** Servicio que pide capturar su nombre (`other_service_name`). */
export const CUSTOM_SERVICE_CATEGORY = C.otro_servicio;

/** Unidad del servicio; un `code` nuevo del catálogo se pregunta al mes con "servicios". */
export const getServiceUnits = (code: string) =>
  SERVICE_UNITS[code] ?? { one: "servicio", many: "servicios", monthly: true };

/** Número de un texto con separadores (`"1,200"` → 1200). */
export const toNumber = (value: string | number) => {
  const n = Number(String(value ?? "").replace(/[^\d.]/g, ""));
  return Number.isFinite(n) ? n : 0;
};

/** Solo dígitos, con separadores de miles (`"12000"` → `"12,000"`). */
export const toThousands = (value: string) => {
  const digits = value.replace(/[^\d]/g, "");
  return digits ? Number(digits).toLocaleString("en-US") : "";
};

/** Cantidad al mes del servicio: directa en los mensuales; día × días en los demás. */
export const getMonthlyQuantity = (service: IncomeService) =>
  getServiceUnits(service.category).monthly ? service.perMonth : service.perDay * service.daysPerMonth;

export const getServiceTotal = (service: IncomeService) => getMonthlyQuantity(service) * toNumber(service.price);

/**
 * Bloque de la pantalla → payload del API. Mensual: `MONTH` con la cantidad al mes; por día: `DAY` con
 * la cantidad al día y los días al mes.
 */
export const toIncomePayload = (service: IncomeService, catalogId: string): ServiceMonthlyIncomePayload => {
  const monthly = getServiceUnits(service.category).monthly;
  return {
    service_monthly_income_catalog_id: catalogId,
    period_type: monthly ? "MONTH" : "DAY",
    average_quantity: monthly ? service.perMonth : service.perDay,
    days_per_month: monthly ? null : service.daysPerMonth,
    price: toNumber(service.price),
    other_service_name: service.category === CUSTOM_SERVICE_CATEGORY ? service.customName.trim() : null,
  };
};
